import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, BadgeCheck, ChevronDown, Brain, Zap, Moon, Shield, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/stores/cartStore";
import AnnouncementBar from "./AnnouncementBar";

const navItems = [
  { label: "La Ciencia", href: "/la-ciencia" },
  { label: "Quiénes Somos", href: "/quienes-somos" },
  
  { label: "Quiz", href: "/quiz" },
  { label: "Journal", href: "/journal" },
  { label: "Contacto", href: "/contacto" },
];

const objetivoLinks = [
  { label: "Enfoque & Cognición", href: "/producto/lions-mane-puro", icon: Brain },
  { label: "Energía & Rendimiento", href: "/producto/cordyceps", icon: Zap },
  { label: "Descanso & Sueño", href: "/producto/reishi-puro", icon: Moon },
  { label: "Inmunidad & Digestión", href: "/producto/chaga-blend", icon: Shield },
  { label: "Bienestar Integral", href: "/producto/mushroom-blend", icon: Sparkles },
];

const protocoloLinks = [
  { label: "Protocolo Enfoque", href: "/tienda/protocolos#enfoque", featured: false },
  { label: "Protocolo Energía", href: "/tienda/protocolos#energia", featured: false },
  { label: "Protocolo Sueño", href: "/tienda/protocolos#sueno", featured: false },
  { label: "Protocolo Completo", href: "/tienda/protocolos#completo", featured: true },
];

interface HeaderProps {
  transparentOnTop?: boolean;
}

const Header = ({ transparentOnTop = false }: HeaderProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const totalItems = useCartStore((s) => s.items.reduce((sum, i) => sum + i.quantity, 0));
  const openCart = useCartStore((s) => s.openCart);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const openDropdown = () => {
    clearTimeout(timeoutRef.current);
    setShopOpen(true);
  };
  const closeDropdown = () => {
    timeoutRef.current = setTimeout(() => setShopOpen(false), 150);
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isTransparent = transparentOnTop && !scrolled && !mobileOpen;
  const location = useLocation();

  const isActive = (href: string) => {
    if (href === "/#productos") return false;
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  return (
    <header className="fixed top-0 w-full z-50">
      {scrolled && <AnnouncementBar />}
      <nav
        className={`transition-all duration-500 ${
          isTransparent
            ? "bg-transparent"
            : "backdrop-blur-md bg-background/90 border-b border-border/60 shadow-sm"
        }`}
      >
        <div className="container flex items-center justify-between h-16 lg:h-[72px]">
          {/* Mobile menu toggle */}
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <X className={`w-5 h-5 ${isTransparent ? "text-white" : "text-foreground"}`} />
            ) : (
              <Menu className={`w-5 h-5 ${isTransparent ? "text-white" : "text-foreground"}`} />
            )}
          </button>

          {/* Logo */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center">
              <span
                className={`text-3xl lg:text-4xl font-heading font-bold tracking-tight lowercase transition-colors duration-500 ${
                  isTransparent ? "text-white" : "text-foreground"
                }`}
              >
                umbo<sup className="text-[10px] lg:text-xs font-normal align-super ml-0.5">®</sup>
              </span>
            </Link>
            {!isTransparent && (
              <div className="hidden lg:flex items-center gap-1.5 bg-secondary/80 border border-border rounded-full px-3 py-1">
                <BadgeCheck className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">
                  Respaldado por Ker Clinic
                </span>
              </div>
            )}
          </div>

          {/* Desktop Nav */}
          <ul className="hidden lg:flex items-center gap-8">
            {/* Tienda dropdown */}
            <li
              className="relative"
              onMouseEnter={openDropdown}
              onMouseLeave={closeDropdown}
              ref={dropdownRef}
            >
              <Link
                to="/#productos"
                className={`text-sm font-medium transition-colors duration-200 flex items-center gap-1 ${
                  isTransparent
                    ? "text-white/80 hover:text-white"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Tienda
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${shopOpen ? "rotate-180" : ""}`}
                />
              </Link>

              {shopOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50">
                  <div className="bg-background border border-border rounded-xl shadow-xl w-[420px] p-5 grid grid-cols-2 gap-6">
                    {/* Por Objetivo */}
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-3">
                        Por Objetivo
                      </p>
                      <ul className="space-y-1">
                        {objetivoLinks.map((item) => (
                          <li key={item.label}>
                            <Link
                              to={item.href}
                              onClick={() => setShopOpen(false)}
                              className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-foreground hover:bg-secondary transition-colors"
                            >
                              <item.icon className="w-4 h-4 text-primary shrink-0" />
                              {item.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/#productos"
                        onClick={() => setShopOpen(false)}
                        className="block mt-3 px-2.5 text-xs font-semibold text-primary hover:underline"
                      >
                        Ver todos los productos →
                      </Link>
                    </div>

                    {/* Protocolos */}
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-3">
                        Protocolos
                      </p>
                      <ul className="space-y-1">
                        {protocoloLinks.map((item) => (
                          <li key={item.label}>
                            <Link
                              to={item.href}
                              onClick={() => setShopOpen(false)}
                              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                                item.featured
                                  ? "bg-primary/5 text-primary font-semibold hover:bg-primary/10"
                                  : "text-foreground hover:bg-secondary"
                              }`}
                            >
                              {item.featured && <Sparkles className="w-4 h-4 shrink-0" />}
                              {item.label}
                              {item.featured && (
                                <span className="ml-auto text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-bold">
                                  -20%
                                </span>
                              )}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </li>

            {navItems.map((item) => (
              <li key={item.label}>
                <Link
                  to={item.href}
                  className={`text-sm font-medium transition-colors duration-200 relative pb-0.5 ${
                    isTransparent
                      ? isActive(item.href)
                        ? "text-white after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-white after:rounded-full"
                        : "text-white/80 hover:text-white"
                      : isActive(item.href)
                        ? "text-foreground after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:rounded-full"
                        : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Utilities */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className={`relative ${
                isTransparent
                  ? "text-white/80 hover:text-white hover:bg-white/10"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={openCart}
            >
              <ShoppingBag className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-umbo-coral text-[10px] font-bold flex items-center justify-center text-primary-foreground">
                  {totalItems}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-border/60 bg-background animate-fade-in">
            <ul className="container py-4 space-y-1">
              {/* Tienda accordion */}
              <li>
                <button
                  className="w-full flex items-center justify-between py-3 px-2 text-base font-medium text-foreground"
                  onClick={() => setMobileShopOpen(!mobileShopOpen)}
                >
                  Tienda
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${mobileShopOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {mobileShopOpen && (
                  <div className="pl-4 pb-3 space-y-4">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2 px-2">
                        Por Objetivo
                      </p>
                      {objetivoLinks.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => {
                            setMobileOpen(false);
                            setMobileShopOpen(false);
                          }}
                          className="flex items-center gap-2.5 px-2 py-2 text-sm text-foreground hover:text-primary transition-colors"
                        >
                          <item.icon className="w-4 h-4 text-primary" />
                          {item.label}
                        </Link>
                      ))}
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2 px-2">
                        Protocolos
                      </p>
                      {protocoloLinks.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => {
                            setMobileOpen(false);
                            setMobileShopOpen(false);
                          }}
                          className={`flex items-center gap-2.5 px-2 py-2 text-sm transition-colors ${
                            item.featured ? "text-primary font-semibold" : "text-foreground hover:text-primary"
                          }`}
                        >
                          {item.featured && <Sparkles className="w-4 h-4" />}
                          {item.label}
                          {item.featured && (
                            <span className="ml-1 text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-bold">
                              -20%
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </li>

              {navItems.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.href}
                    className="block py-3 px-2 text-base font-medium text-foreground hover:text-primary transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
